import React, { useEffect, useMemo, useState } from 'react'
import { Collapse, Flex, Text } from '@chakra-ui/core'
import { useFormContext } from 'react-hook-form'
import { CircleMinus, CirclePlus } from 'lucide-react'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { StyledFormControl } from 'components/AdminConsole/commonUI/styles'
import { PasteIdsDialog } from './PasteIdsDialog'
import { stripBidi, wrapBidi } from '../utils'
import { FixedSizeList as List, ListChildComponentProps } from 'react-window'
import LocaleText from 'components/LocaleText'
import useLocaleText from 'components/useLocaleText'
import { useItemIds, Item } from './useItemsIds'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'

const MAX_VISIBLE = 3
const ROW_HEIGHT = 60
const VIRTUALIZATION_THRESHOLD = 50

const FieldRow: React.FC<{
  field: Item
  fields: Item[]
  index: number
  isDuplicate: boolean
  onChange: (index: number, value: string) => void
  onRemove: (index: number) => void
}> = ({ field, fields, index, isDuplicate, onChange, onRemove }) => {
  const isRtl = useSelector(isRtlSelector)

  const canRemove = fields.length > 1

  return (
    <Flex w={{ base: '100%', md: '50%' }} align="center" mb="10px" style={{ columnGap: 8 }}>
      <StyledFormControl w="100%" isInvalid={isDuplicate}>
        <InputWithIcon
          w="100%"
          placeholder="Enter ID"
          autoComplete="item-id"
          dir="ltr"
          textAlign={isRtl ? 'right' : 'left'}
          style={{ unicodeBidi: 'plaintext' }}
          value={wrapBidi(field.value || '')}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            const clean = stripBidi(wrapBidi(e.currentTarget.value))
            onChange(index, clean)
          }}
        />
      </StyledFormControl>

      <CircleMinus
        size={20}
        color="#555"
        style={{ cursor: 'pointer', pointerEvents: canRemove ? 'auto' : 'none', opacity: canRemove ? 1 : 0.5 }}
        onClick={() => onRemove(index)}
      />
    </Flex>
  )
}

type VirtualData = {
  items: Item[]
  isDuplicateAt: (index: number) => boolean
  onChange: (index: number, value: string) => void
  onRemove: (index: number) => void
}

const VirtualRow: React.FC<ListChildComponentProps<VirtualData>> = ({ index, style, data }) => {
  const item = data.items[index]
  return (
    <div style={style}>
      <FieldRow
        key={item.id}
        field={item}
        index={index}
        isDuplicate={data.isDuplicateAt(index)}
        onChange={data.onChange}
        onRemove={data.onRemove}
        fields={data.items}
      />
    </div>
  )
}

export const ItemIdsList: React.FC = () => {
  const isRTL = useSelector(isRtlSelector)
  const { errors, watch } = useFormContext()
  const itemType: string = watch('products_cluster_data.item_type')
  const max_items_error_1 = useLocaleText('max_items_error_1', true)
  const max_items_error_2 = useLocaleText('max_items_error_2', true)
  const t_add_id = useLocaleText('add_id', true)

  const { items, addItem, addIds, removeItem, updateItem, isDuplicateAt } = useItemIds('products_cluster_data.item')

  const [isExpanded, setIsExpanded] = useState(items.length > MAX_VISIBLE)
  const visible = useMemo(() => items.slice(0, MAX_VISIBLE), [items])
  const hidden = useMemo(() => items.slice(MAX_VISIBLE), [items])

  useEffect(() => {
    setIsExpanded(items.length > MAX_VISIBLE)
  }, [items.length])
  const shouldUseVirtualization = items.length > VIRTUALIZATION_THRESHOLD

  if (!itemType) return null

  return (
    <Flex w="100%" direction="column" style={{ rowGap: '10px' }}>
      {!!errors?.products_cluster_data?.item && (
        <Text color="red.500" fontSize="14px" fontWeight="medium">
          {errors.products_cluster_data.item.message || `${max_items_error_1} / ${max_items_error_2}`}
        </Text>
      )}

      {shouldUseVirtualization ? (
        <List
          height={Math.min(400, items.length * ROW_HEIGHT)}
          itemCount={items.length}
          itemSize={ROW_HEIGHT}
          width="100%"
          style={{ direction: isRTL ? 'rtl' : 'ltr' }}
          itemKey={(index) => items[index].id}
          itemData={{
            items,
            isDuplicateAt,
            onChange: updateItem,
            onRemove: removeItem,
          }}
        >
          {VirtualRow}
        </List>
      ) : (
        <>
          <Flex wrap="wrap" style={{ columnGap: 10 }}>
            {visible.map((it, i) => (
              <FieldRow
                key={it.id}
                field={it}
                index={i}
                isDuplicate={isDuplicateAt(i)}
                onChange={updateItem}
                onRemove={removeItem}
                fields={items}
              />
            ))}
          </Flex>

          {hidden.length > 0 && (
            <Collapse isOpen={isExpanded} animateOpacity>
              <Flex wrap="wrap" style={{ columnGap: 10 }}>
                {hidden.map((it, i) => {
                  const idx = MAX_VISIBLE + i
                  return (
                    <FieldRow
                      key={it.id}
                      field={it}
                      index={idx}
                      isDuplicate={isDuplicateAt(idx)}
                      onChange={updateItem}
                      onRemove={removeItem}
                      fields={items}
                    />
                  )
                })}
              </Flex>
            </Collapse>
          )}
        </>
      )}

      <Flex
        w={{ base: '100%', md: '50%' }}
        justify="space-between"
        align="center"
        pl={isRTL ? '25px' : '10px'}
        pr={isRTL ? '10px' : '25px'}
      >
        <Flex align="center" style={{ columnGap: 8 }}>
          <NoFocusButton size="sm" variantColor="black" variant="outline" style={{ columnGap: 4 }}>
            <Text>{t_add_id}</Text>
            <CirclePlus size={18} onClick={addItem} />
          </NoFocusButton>

          <PasteIdsDialog onConfirm={addIds} />
        </Flex>

        {items.length > MAX_VISIBLE && !shouldUseVirtualization && (
          <Text
            fontSize="14px"
            onClick={() => setIsExpanded((s) => !s)}
            color="#4a4a4a"
            cursor="pointer"
            textDecoration="underline"
          >
            <LocaleText text={isExpanded ? 'show_less' : 'see_more'} />
          </Text>
        )}
      </Flex>
    </Flex>
  )
}
